import { useCallback, useState } from 'react';

import type { UUID } from '@aline/common';
import { defaultDropAnimationSideEffects, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent, DragStartEvent, DropAnimation } from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import {
  SelectiveKeyboardSensor,
  SelectiveMouseSensor,
  SelectivePointerSensor,
} from 'src/components/workflow/board/utils/keyboardCoordinateGetter';
import type { Apollo as ApolloTypes } from 'src/gql/generated';
import { useReorderPhases } from 'src/hooks/phase';

export const phasesDropAnimation: DropAnimation = {
  duration: 150,
  easing: 'ease-out',
  sideEffects: defaultDropAnimationSideEffects({
    styles: { active: { opacity: '0.4' } },
  }),
};

export function usePhasesDnd(
  phases: ApolloTypes.PhaseSimpleFragment[],
  workflowId: UUID,
  readOnly = false
) {
  const { reorderPhases } = useReorderPhases();
  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(SelectivePointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
    useSensor(SelectiveKeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    useSensor(SelectiveMouseSensor, { activationConstraint: { distance: 8 } })
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      if (readOnly) return;
      setActiveId(String(event.active.id));
    },
    [readOnly]
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      setActiveId(null);
      if (readOnly) return;
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const phaseIds = phases.map(p => p.id);
      const oldIndex = phaseIds.indexOf(active.id as UUID);
      const newIndex = phaseIds.indexOf(over.id as UUID);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove<UUID>(phaseIds, oldIndex, newIndex);
      if (reordered.some((id, i) => id !== phaseIds[i])) {
        await reorderPhases({ variables: { input: { phaseIds: reordered, workflowId } } });
      }
    },
    [phases, readOnly, reorderPhases, workflowId]
  );

  return {
    activeDraggingPhase: phases.find(p => p.id === activeId) ?? null,
    activeDraggingPhaseId: activeId,
    dndHandlers: {
      onDragCancel: () => {
        setActiveId(null);
      },
      onDragEnd: handleDragEnd,
      onDragStart: handleDragStart,
    },
    sensors,
  };
}
